import React from "react";

import ReactPDF, {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";

type ContractSectionType =
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "variable"
  | "signatures";
type AlignementType = "leftAlign" | "centerAlign" | "rightAlign";

type ContractSectionChild = {
  text: string;
  italic?: boolean;
  bold?: boolean;
  underline?: boolean;
  headingOne?: boolean;
  headingTwo?: boolean;
};

type SectionChildren = ContractSectionChild | ContractSection;

type ContractSection = {
  type: ContractSectionType;
  alignment?: AlignementType;
  value?: string;
  children: SectionChildren[];
};

type ContractSignatureSection = ContractSection & {
  signatories: {
    collaborator: boolean;
    referent: boolean;
    administrator: boolean;
  };
  docusignTag: string;
};

const isContractSectionChild = (
  child: SectionChildren
): child is ContractSectionChild => !!(child as ContractSectionChild)?.text;

const isContractSection = (
  child: SectionChildren
): child is ContractSection => {
  return !!(child as ContractSection)?.type;
};

const isContractSignatureSection = (
  child: SectionChildren
): child is ContractSignatureSection => {
  return (
    !!(child as ContractSection)?.type &&
    (child as ContractSection).type === "signatures"
  );
};

Font.register({
  family: "Bitter",
  fonts: [
    {
      fontWeight: 400,
      src: require("../../../assets/fonts/Bitter-Regular.ttf"),
    },
    {
      fontWeight: 400,
      fontStyle: "italic",
      src: require("../../../assets/fonts/Bitter-Italic.ttf"),
    },
    {
      fontWeight: 700,
      src: require("../../../assets/fonts/Bitter-Bold.ttf"),
    },
    {
      fontWeight: 700,
      fontStyle: "italic",
      src: require("../../../assets/fonts/Bitter-BoldItalic.ttf"),
    },
    {
      fontWeight: 800,
      src: require("../../../assets/fonts/Bitter-ExtraBold.ttf"),
    },
    {
      fontWeight: 800,
      fontStyle: "italic",
      src: require("../../../assets/fonts/Bitter-ExtraBoldItalic.ttf"),
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Bitter",
    fontWeight: "normal",
    display: "flex",
    paddingVertical: "25mm",
    paddingHorizontal: "25mm",
    fontSize: "12pt",
  },
  docusignTag: {
    margin: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 6,
    width: "33%",
    color: "white",
    border: "1pt solid #e7e5ed",
  },
  bold: {
    fontWeight: "bold",
  },
  underline: {
    textDecoration: "underline",
  },
  italic: {
    fontStyle: "italic",
  },
  headingOne: {
    fontWeight: "extrabold",
    fontSize: "14.5pt",
  },
  headingTwo: {
    fontWeight: "bold",
    fontSize: "18pt",
  },
  leftAlign: {
    textAlign: "left",
  },
  rightAlign: {
    textAlign: "right",
  },
  centerAlign: {
    textAlign: "center",
  },
});

const getTextStyle = (child: ContractSectionChild): ReactPDF.Style => ({
  ...(child?.bold ? styles.bold : {}),
  ...(child?.italic ? styles.italic : {}),
  ...(child?.underline ? styles.underline : {}),
  ...(child?.headingTwo ? styles.headingTwo : {}),
  ...(child?.headingOne ? styles.headingOne : {}),
});

const getParagraphStyle = (section: ContractSection): ReactPDF.Style => ({
  ...(section?.alignment === "leftAlign" ? styles.leftAlign : {}),
  ...(section?.alignment === "centerAlign" ? styles.centerAlign : {}),
  ...(section?.alignment === "rightAlign" ? styles.rightAlign : {}),
});

type ContractSignatureSectionProps = {
  section: ContractSignatureSection;
};

const ContractSignatureSection: React.FC<ContractSignatureSectionProps> = ({
  section,
}) => {
  return <Text style={styles.docusignTag}>{section.docusignTag}</Text>;
};

type ContractSectionProps = {
  section: ContractSection;
};

const passValueAsChildren = (section: ContractSection): ContractSection => ({
  ...section,
  children: [{ ...section.children[0], text: section.value }],
});

const ContractSection: React.FC<ContractSectionProps> = ({ section }) => {
  return (
    <>
      {section.children.map((child, index) => (
        <>
          {isContractSectionChild(child) && (
            <Text style={getTextStyle(child)} key={`${child.text}${index}`}>
              {child.text}
            </Text>
          )}
          {isContractSection(child) && (
            <ContractSection
              section={child.value ? passValueAsChildren(child) : child}
              key={`${child.type}${index}`}
            />
          )}
        </>
      ))}
    </>
  );
};

type ContractDocumentProps = {
  sections: ContractSection[];
  pageSize: "A4" | "LETTER";
};

const ContractDocument: React.FC<ContractDocumentProps> = ({
  sections,
  pageSize,
}) => {
  return (
    <Document>
      <Page size={pageSize} style={styles.page}>
        {sections.map((section, idx) => (
          <View key={`Section ${idx + 1}`}>
            {isContractSignatureSection(section) ? (
              <ContractSignatureSection section={section} />
            ) : (
              <Text style={getParagraphStyle(section)}>
                <ContractSection section={section} />
                {"\n"}
              </Text>
            )}
          </View>
        ))}
      </Page>
    </Document>
  );
};

type Contract = {
  sections: ContractSection[];
  name: string;
};

export const generatePdfDocument = async (
  contract: Contract,
  callback: (b64PDF: string, filename: string) => void
) => {
  const reader = new FileReader();
  const blob = await pdf(
    <ContractDocument sections={contract.sections} pageSize="A4" />
  ).toBlob();

  reader.readAsDataURL(blob);
  reader.onloadend = () => callback(reader.result as string, contract.name);
};
